import React, { useState } from 'react';
import Modal from '../Modal';

interface AddCarrierModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (carrier: CarrierData) => void;
}

interface CarrierData {
  nom: string;
  adresse?: string;
  telephone?: string;
  email?: string;
  zone?: string;
  statut?: 'actif' | 'inactif';
}

const AddCarrierModal: React.FC<AddCarrierModalProps> = ({ isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState<CarrierData>({
    nom: '',
    adresse: '',
    telephone: '',
    email: '',
    zone: '',
    statut: 'actif'
  });

  const [errors, setErrors] = useState<Partial<CarrierData>>({});

  const handleInputChange = (field: keyof CarrierData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<CarrierData> = {};

    if (!formData.nom.trim()) {
      newErrors.nom = 'Le nom est requis';
    }
    

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      onSave(formData);
      onClose();
      setFormData({
        nom: '',
        adresse: '',
        telephone: '',
        email: '',
        zone: '',
        statut: 'actif'
      });
      setErrors({});
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Ajouter un nouveau transporteur" size="lg">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Nom et Zone */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nom <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.nom}
              onChange={(e) => handleInputChange('nom', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                errors.nom ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Nom du transporteur"
            />
            {errors.nom && (
              <p className="mt-1 text-sm text-red-600">{errors.nom}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Zone 
            </label>
            <select
              value={formData.zone}
              onChange={(e) => handleInputChange('zone', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                errors.zone ? 'border-red-300' : 'border-gray-300'
              }`}
            >
              <option value="">Sélectionner une zone</option>
              <option value="nord">Zone Nord</option>
              <option value="sud">Zone Sud</option>
              <option value="est">Zone Est</option>
              <option value="ouest">Zone Ouest</option>
              <option value="centre">Zone Centre</option>
            </select>
            {errors.zone && (
              <p className="mt-1 text-sm text-red-600">{errors.zone}</p>
            )}
          </div>
        </div>

        {/* Adresse */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Adresse 
          </label>
          <textarea
            value={formData.adresse}
            onChange={(e) => handleInputChange('adresse', e.target.value)}
            rows={3}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
              errors.adresse ? 'border-red-300' : 'border-gray-300'
            }`}
            placeholder="Adresse complète du transporteur"
          />
          {errors.adresse && (
            <p className="mt-1 text-sm text-red-600">{errors.adresse}</p>
          )}
        </div>

        {/* Téléphone et Email */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Téléphone
            </label>
            <input
              type="tel"
              value={formData.telephone}
              onChange={(e) => handleInputChange('telephone', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                errors.telephone ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="01 23 45 67 89"
            />
            {errors.telephone && (
              <p className="mt-1 text-sm text-red-600">{errors.telephone}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              placeholder="contact@transporteur.com"
            />
          </div>
        </div>

        {/* Statut */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Statut
          </label>
          <select
            value={formData.statut}
            onChange={(e) => handleInputChange('statut', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
          >
            <option value="actif">Actif</option>
            <option value="inactif">Inactif</option>
          </select>
        </div>

        {/* Boutons d'action */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Annuler
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
          >
            Ajouter le transporteur
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default AddCarrierModal;
